<template>
<div class="code-examples">
    <div class="code-tabs-shell">
        <div class="code-tabs">
            <button
                v-for="lang in languages"
                :key="lang.id"
                class="code-tab"
                :class="{ active: activeTab === lang.id }"
                @click="setActiveTab(lang.id)"
                type="button"
            >// {{ lang.name }}</button>
        </div>
    </div>
    <div class="code-block">
        <div class="code-toolbar">
            <span>GET /a1/reviews &middot; 200 OK &middot; 132ms</span>
            <button @click="copyCode">{{ justCopied ? 'Copied' : 'Copy' }}</button>
        </div>
        <div class="code-pane active">
            <div class="code-lines">{{ lineNumbers }}</div>
            <pre class="code-pre"><code :class="'language-' + currentLanguage.highlight" v-html="currentCode"></code></pre>
        </div>
    </div>
</div>
</template>

<script>
// highlight.js is loaded lazily on mount so it stays out of the initial
// bundle (the raw, escaped code renders immediately; colors apply when the
// chunk arrives — usually within a frame or two).
let hljsPromise = null
function loadHljs() {
    if (!hljsPromise) {
        hljsPromise = Promise.all([
            import('highlight.js/lib/core'),
            import('highlight.js/lib/languages/python'),
            import('highlight.js/lib/languages/javascript'),
            import('highlight.js/lib/languages/php'),
            import('highlight.js/lib/languages/ruby'),
            import('highlight.js/lib/languages/go'),
            import('highlight.js/lib/languages/java'),
            import('highlight.js/lib/languages/csharp'),
            import('highlight.js/lib/languages/swift'),
            import('highlight.js/lib/languages/kotlin'),
            import('highlight.js/lib/languages/bash'),
            import('highlight.js/lib/languages/typescript'),
            import('highlight.js/lib/languages/cpp'),
        ]).then(([core, python, javascript, php, ruby, go, java, csharp, swift, kotlin, bash, typescript, cpp]) => {
            const hljs = core.default
            hljs.registerLanguage('python', python.default)
            hljs.registerLanguage('javascript', javascript.default)
            hljs.registerLanguage('php', php.default)
            hljs.registerLanguage('ruby', ruby.default)
            hljs.registerLanguage('go', go.default)
            hljs.registerLanguage('java', java.default)
            hljs.registerLanguage('csharp', csharp.default)
            hljs.registerLanguage('swift', swift.default)
            hljs.registerLanguage('kotlin', kotlin.default)
            hljs.registerLanguage('bash', bash.default)
            hljs.registerLanguage('typescript', typescript.default)
            hljs.registerLanguage('cpp', cpp.default)
            return hljs
        })
    }
    return hljsPromise
}

function escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export default {
    name: 'CodeExamples',
    data() {
        return {
            activeTab: 'python',
            justCopied: false,
            hljs: null,
            // Signed-in users see their real access key in the examples
            // (hikerapi pattern) — filled by fetchUserToken() below.
            userAccessKey: null,
            languages: [
                { id: 'python', name: 'python', highlight: 'python' },
                { id: 'curl', name: 'curl', highlight: 'bash' },
                { id: 'javascript', name: 'javascript', highlight: 'javascript' },
                { id: 'typescript', name: 'typescript', highlight: 'typescript' },
                { id: 'go', name: 'go', highlight: 'go' },
                { id: 'php', name: 'php', highlight: 'php' },
                { id: 'ruby', name: 'ruby', highlight: 'ruby' },
                { id: 'java', name: 'java', highlight: 'java' },
                { id: 'csharp', name: 'c#', highlight: 'csharp' },
                { id: 'kotlin', name: 'kotlin', highlight: 'kotlin' },
                { id: 'swift', name: 'swift', highlight: 'swift' },
                { id: 'cpp', name: 'c++', highlight: 'cpp' },
            ],
        }
    },
    computed: {
        apiBase() { return 'https://api.asoradar.com' },
        accessKey() { return this.userAccessKey || 'YOUR_ACCESS_KEY' },
        currentLanguage() {
            return this.languages.find(l => l.id === this.activeTab) || this.languages[0]
        },
        currentCode() {
            const code = this.getCodeForLanguage(this.activeTab)
            if (!this.hljs) return escapeHtml(code)
            try {
                return this.hljs.highlight(code, { language: this.currentLanguage.highlight }).value
            } catch (e) {
                return escapeHtml(code)
            }
        },
        currentCodeRaw() { return this.getCodeForLanguage(this.activeTab) },
        lineNumbers() {
            const lines = this.currentCodeRaw.split('\n').length
            return Array.from({ length: lines }, (_, i) => i + 1).join('\n')
        },
    },
    methods: {
        setActiveTab(tabId) { this.activeTab = tabId },
        copyCode() {
            const code = this.currentCodeRaw
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(code).then(() => {
                    this.justCopied = true
                    setTimeout(() => { this.justCopied = false }, 1600)
                }).catch(() => this.fallbackCopy(code))
            } else {
                this.fallbackCopy(code)
            }
        },
        fallbackCopy(text) {
            const ta = document.createElement('textarea')
            ta.value = text
            ta.style.position = 'fixed'
            ta.style.left = '-9999px'
            document.body.appendChild(ta)
            ta.select()
            try {
                document.execCommand('copy')
                this.justCopied = true
                setTimeout(() => { this.justCopied = false }, 1600)
            } catch (e) {}
            document.body.removeChild(ta)
        },
        dedent(str) {
            const lines = str.split('\n')
            if (lines[0] === '') lines.shift()
            const minIndent = lines
                .filter(l => l.trim())
                .reduce((min, l) => Math.min(min, l.match(/^(\s*)/)[1].length), Infinity)
            return lines.map(l => l.slice(minIndent)).join('\n').trim()
        },
        getCodeForLanguage(lang) {
            const base = this.apiBase
            const codes = {
                python: `# pip install asoradar
from asoradar import ASORadar  # official SDK — sync + async, typed

client = ASORadar(access_key="YOUR_ACCESS_KEY")

# Get App Store reviews — cache-first, transparent realtime fetch on stale data
reviews = client.app_store.reviews(app_id="389801252", rating=5, search="amazing", count=10)
print(f"Found {reviews['total']} reviews")

# Resolve app metadata, search, top charts, suggestions, and similar apps
app = client.app_store.app("389801252")
results = client.app_store.search(term="meditation", num=10)
top_free = client.app_store.list(collection="topfreeapplications", num=25)
suggest = client.app_store.suggest(term="med")
similar = client.app_store.similar("389801252")

# Same shape on Google Play (client.google_play, plus optional lang)
gp = client.google_play.app("com.spotify.music", lang="en")

# asyncio? \`from asoradar import AsyncASORadar\` — same surface, await-able`,

                javascript: `// npm install asoradar
import { ASORadar } from 'asoradar'; // official SDK — typed, ESM + CJS, zero deps

const client = new ASORadar({ accessKey: 'YOUR_ACCESS_KEY' });

// Get App Store reviews — cache-first, transparent realtime fetch on stale data
const reviews = await client.appStore.reviews({
    app_id: '389801252', rating: 5, search: 'amazing', count: 10,
});
console.log(\`Found \${reviews.total} reviews\`);

// Metadata, search, charts, suggestions, similar
const app = await client.appStore.app('389801252');
const results = await client.appStore.search({ term: 'meditation', num: 10 });
const topFree = await client.appStore.list({ collection: 'topfreeapplications', num: 25 });
const suggest = await client.appStore.suggest({ term: 'med' });
const similar = await client.appStore.similar('389801252');

// Google Play — same shape via client.googlePlay
const gp = await client.googlePlay.app('com.spotify.music', { lang: 'en' });`,

                curl: `# App Store reviews
curl "${base}/a1/reviews?access_key=YOUR_ACCESS_KEY&app_id=389801252&rating=5&search=amazing&count=10"

# Force a realtime fetch even if cache is fresh
curl "${base}/a1/reviews?access_key=YOUR_ACCESS_KEY&app_id=389801252&fresh=true"

# Full App Store metadata, search, top charts, autocomplete, and similar apps
curl "${base}/a1/app/389801252?access_key=YOUR_ACCESS_KEY"
curl "${base}/a1/search?access_key=YOUR_ACCESS_KEY&term=meditation&num=10"
curl "${base}/a1/list?access_key=YOUR_ACCESS_KEY&collection=topfreeapplications&num=25"
curl "${base}/a1/suggest?access_key=YOUR_ACCESS_KEY&term=med"
curl "${base}/a1/similar/389801252?access_key=YOUR_ACCESS_KEY"

# Google Play — same shape, plus optional lang
curl "${base}/g1/reviews?access_key=YOUR_ACCESS_KEY&app_id=com.instagram.android&lang=en&count=10"
curl "${base}/g1/app/com.spotify.music?access_key=YOUR_ACCESS_KEY&lang=en"`,

                php: `<?php
$accessKey = 'YOUR_ACCESS_KEY';
$base = '${base}';

function apiGet($endpoint, $params = []) {
    global $accessKey, $base;
    $params['access_key'] = $accessKey;
    $url = $base . $endpoint . '?' . http_build_query($params);
    $response = file_get_contents($url);
    return json_decode($response, true);
}

$reviews = apiGet('/a1/reviews', [
    'app_id' => '389801252', 'rating' => 5, 'search' => 'amazing', 'count' => 10,
]);
echo "Found {$reviews['total']} reviews\\n";`,

                go: `package main

import (
    "encoding/json"
    "fmt"
    "io"
    "net/http"
    "net/url"
)

const (
    accessKey = "YOUR_ACCESS_KEY"
    baseURL   = "${base}"
)

func apiGet(endpoint string, params map[string]string) map[string]interface{} {
    u, _ := url.Parse(baseURL + endpoint)
    q := u.Query()
    q.Set("access_key", accessKey)
    for k, v := range params {
        q.Set(k, v)
    }
    u.RawQuery = q.Encode()
    resp, _ := http.Get(u.String())
    defer resp.Body.Close()
    body, _ := io.ReadAll(resp.Body)
    var result map[string]interface{}
    json.Unmarshal(body, &result)
    return result
}

func main() {
    reviews := apiGet("/a1/reviews", map[string]string{
        "app_id": "389801252", "rating": "5", "count": "10",
    })
    fmt.Printf("Found %.0f reviews\\n", reviews["total"])
}`,

                ruby: `require 'net/http'
require 'json'

ACCESS_KEY = 'YOUR_ACCESS_KEY'
BASE = '${base}'

def api_get(endpoint, params = {})
  params[:access_key] = ACCESS_KEY
  uri = URI("#{BASE}#{endpoint}")
  uri.query = URI.encode_www_form(params)
  JSON.parse(Net::HTTP.get(uri))
end

# Get App Store reviews
reviews = api_get('/a1/reviews', app_id: '389801252', rating: 5, search: 'amazing', count: 10)
puts "Found #{reviews['total']} reviews"

reviews['items'].each do |r|
  puts "  [#{r['rating']}] #{r['author']}: #{r['text'][0..80]}"
end`,

                java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import org.json.JSONObject;

public class ASORadarExample {
    static final String ACCESS_KEY = "YOUR_ACCESS_KEY";
    static final String BASE = "${base}";
    static final HttpClient client = HttpClient.newHttpClient();

    static JSONObject apiGet(String endpoint, String params) throws Exception {
        String url = BASE + endpoint + "?access_key=" + ACCESS_KEY + "&" + params;
        HttpRequest req = HttpRequest.newBuilder()
            .uri(URI.create(url)).GET().build();
        HttpResponse<String> resp = client.send(req, HttpResponse.BodyHandlers.ofString());
        return new JSONObject(resp.body());
    }

    public static void main(String[] args) throws Exception {
        JSONObject reviews = apiGet("/a1/reviews", "app_id=389801252&rating=5&count=10");
        System.out.printf("Found %d reviews%n", reviews.getInt("total"));
    }
}`,

                csharp: `using System.Net.Http;
using System.Text.Json;

const string AccessKey = "YOUR_ACCESS_KEY";
const string Base = "${base}";
var client = new HttpClient();

async Task<JsonDocument> ApiGet(string endpoint, string query) {
    var url = $"{Base}{endpoint}?access_key={AccessKey}&{query}";
    var json = await client.GetStringAsync(url);
    return JsonDocument.Parse(json);
}

var reviews = await ApiGet("/a1/reviews", "app_id=389801252&rating=5&count=10");
var total = reviews.RootElement.GetProperty("total").GetInt32();
Console.WriteLine($"Found {total} reviews");`,

                typescript: `const ACCESS_KEY: string = 'YOUR_ACCESS_KEY';
const BASE: string = '${base}';

interface ReviewsResponse {
    total: number;
    items: Array<{
        review_id: string;
        author: string;
        rating: number;
        title: string;
        text: string;
    }>;
}

async function apiGet<T>(endpoint: string, params: Record<string, string> = {}): Promise<T> {
    const url = new URL(BASE + endpoint);
    url.searchParams.set('access_key', ACCESS_KEY);
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    const resp = await fetch(url.toString());
    return resp.json();
}

const reviews = await apiGet<ReviewsResponse>('/a1/reviews', {
    app_id: '389801252', rating: '5', search: 'amazing', count: '10',
});
console.log(\`Found \${reviews.total} reviews\`);`,

                kotlin: `import java.net.URI
import java.net.http.HttpClient
import java.net.http.HttpRequest
import java.net.http.HttpResponse
import org.json.JSONObject

const val ACCESS_KEY = "YOUR_ACCESS_KEY"
const val BASE = "${base}"
val client: HttpClient = HttpClient.newHttpClient()

fun apiGet(endpoint: String, params: String): JSONObject {
    val url = "$BASE$endpoint?access_key=$ACCESS_KEY&$params"
    val req = HttpRequest.newBuilder().uri(URI.create(url)).GET().build()
    val resp = client.send(req, HttpResponse.BodyHandlers.ofString())
    return JSONObject(resp.body())
}

fun main() {
    val reviews = apiGet("/a1/reviews", "app_id=389801252&rating=5&count=10")
    println("Found \${reviews.getInt("total")} reviews")
}`,

                swift: `import Foundation

let accessKey = "YOUR_ACCESS_KEY"
let base = "${base}"

func apiGet(endpoint: String, params: [String: String] = [:]) async throws -> [String: Any] {
    var components = URLComponents(string: base + endpoint)!
    var items = params.map { URLQueryItem(name: $0.key, value: $0.value) }
    items.append(URLQueryItem(name: "access_key", value: accessKey))
    components.queryItems = items
    let (data, _) = try await URLSession.shared.data(from: components.url!)
    return try JSONSerialization.jsonObject(with: data) as! [String: Any]
}

Task {
    let reviews = try await apiGet(
        endpoint: "/a1/reviews",
        params: ["app_id": "389801252", "rating": "5", "count": "10"]
    )
    print("Found \\(reviews["total"]!) reviews")
}`,

                cpp: `#include <iostream>
#include <string>
#include <curl/curl.h>
#include <nlohmann/json.hpp>
using json = nlohmann::json;

const std::string ACCESS_KEY = "YOUR_ACCESS_KEY";
const std::string BASE = "${base}";

size_t write_cb(void* p, size_t sz, size_t n, std::string* s) {
    s->append((char*)p, sz * n);
    return sz * n;
}

json api_get(const std::string& endpoint, const std::string& params) {
    CURL* curl = curl_easy_init();
    std::string resp;
    std::string url = BASE + endpoint + "?access_key=" + ACCESS_KEY + "&" + params;
    curl_easy_setopt(curl, CURLOPT_URL, url.c_str());
    curl_easy_setopt(curl, CURLOPT_WRITEFUNCTION, write_cb);
    curl_easy_setopt(curl, CURLOPT_WRITEDATA, &resp);
    curl_easy_perform(curl);
    curl_easy_cleanup(curl);
    return json::parse(resp);
}

int main() {
    auto reviews = api_get("/a1/reviews", "app_id=389801252&rating=5&count=10");
    std::cout << "Found " << reviews["total"] << " reviews" << std::endl;
    return 0;
}`,
            }
            // One substitution point covers every language snippet: logged-in
            // users get their real key, visitors keep the placeholder.
            return this.dedent(codes[lang] || codes.python)
                .replaceAll('YOUR_ACCESS_KEY', this.accessKey)
        },
        fetchUserToken() {
            this.$services.Token().get().then(resp => {
                const tokens = resp.body && resp.body.data
                if (!tokens || tokens.length === 0) return
                this.userAccessKey = tokens[0].access_key
            }).catch(() => {})
        },
        _onContextLoaded(ctx) {
            if (ctx?.id) {
                this.$bus.off('context:loaded', this._onContextLoaded)
                this.fetchUserToken()
            }
        },
    },
    created() {
        loadHljs().then(h => { this.hljs = h })
        if (this.$state.context?.id) {
            this.fetchUserToken()
        } else {
            this.$bus.on('context:loaded', this._onContextLoaded)
        }
    },
    beforeUnmount() {
        this.$bus.off('context:loaded', this._onContextLoaded)
    },
}
</script>

<style scoped>
.code-examples { width: 100%; }
</style>
